import { cn } from "@/lib/utils";
import { CheckCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export enum NotificationType {
    ROLE_CHANGED = "roleChanged",
    LICENSE_CHANGED = "licenseChanged",
}

export type Notification = {
    type: NotificationType;
    email: string;
};

interface UserNotificationProps {
    notification?: Notification;
}

const NOTIFICATION_CONFIG = {
    [NotificationType.ROLE_CHANGED]: {
        titleKey: "settings.userManagement.roleChanged.title",
        descriptionKey: "settings.userManagement.roleChanged.description",
    },
    [NotificationType.LICENSE_CHANGED]: {
        titleKey: "settings.userManagement.licenseChanged.title",
        descriptionKey: "settings.userManagement.licenseChanged.description",
    },
};

const UserNotification = ({ notification }: UserNotificationProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (notification) {
            setOpen(true);
        }
    }, [notification]);

    if (!notification) {
        return null;
    }

    const config = NOTIFICATION_CONFIG[notification.type];

    return (
        <div
            className={cn(
                "flex items-center gap-2 overflow-hidden rounded-md text-success-base transition-all duration-300",
                open
                    ? "h-17 border border-success-base bg-success-lightest px-4 py-3 opacity-100"
                    : "h-0 border-transparent bg-transparent p-0 opacity-0",
            )}
        >
            <CheckCircle className="mr-2 h-5 w-5" />
            <div className="flex-1 text-sm font-semibold">
                {t(config.titleKey)}
                <br />
                <span className="text-xs font-medium text-text-mainTitle">
                    {t(config.descriptionKey, { email: notification.email })}
                </span>
            </div>
            <button
                className="ml-4 text-text-active hover:text-text-activeHover"
                onClick={() => setOpen(false)}
                type="button"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
};

export default UserNotification;
